import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { InputField } from "@/components/InputField/InputField";
import { SelectField } from "@/components/SelectField/SelectField";
import { Icon } from "@/components/Icon/Icon";

import { GreetingStyleEntry } from "../../../schema/copywritingSettings";

const CHANNEL_OPTIONS = [
	{ value: "linkedin", title: "LinkedIn" },
	{ value: "email", title: "Email" },
];

const MESSAGE_TYPE_OPTIONS = [
	{ value: "connection_request", title: "Connection Request" },
	{ value: "li_introduction", title: "LI Introduction" },
	{ value: "li_follow_up", title: "LI Follow-up" },
	{ value: "li_breakup", title: "LI Breakup" },
	{ value: "email_introduction", title: "Email Introduction" },
	{ value: "email_follow_up", title: "Email Follow-up" },
	{ value: "email_breakup", title: "Email Breakup" },
];

const emptyEntry: GreetingStyleEntry = {
	channel: "linkedin",
	messageType: "connection_request",
	greeting: "",
};

interface GreetingStyleSectionProps {
	value: GreetingStyleEntry[];
	onChange: (value: GreetingStyleEntry[]) => void;
}

export const GreetingStyleSection: FunctionComponent<GreetingStyleSectionProps> = ({
	value,
	onChange,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`copywritingSettings.greetingStyle.${key}`);

	const updateEntry = (index: number, field: keyof GreetingStyleEntry, val: string) => {
		const updated = [...value];

		updated[index] = { ...updated[index], [field]: val };
		onChange(updated);
	};

	return (
		<div className="flex flex-col gap-3">
			<p className="text-sm text-text-meta">{ts("description")}</p>

			{value.length > 0 && (
				<div className="flex items-center gap-3 text-xs font-medium text-text-meta uppercase tracking-wide">
					<div className="w-40">{ts("channel")}</div>
					<div className="w-48">{ts("messageType")}</div>
					<div className="flex-1">{ts("greetingLabel")}</div>
					<div className="w-8" />
				</div>
			)}

			{value.map((entry, index) => (
				<div key={index} className="flex items-start gap-3">
					<div className="w-40">
						<SelectField
							handleSelect={(v) => updateEntry(index, "channel", v as string)}
							name={`channel-${index}`}
							options={CHANNEL_OPTIONS}
							placeholder={ts("channel")}
							selectedOption={entry.channel}
						/>
					</div>
					<div className="w-48">
						<SelectField
							handleSelect={(v) => updateEntry(index, "messageType", v as string)}
							name={`messageType-${index}`}
							options={MESSAGE_TYPE_OPTIONS}
							placeholder={ts("messageType")}
							selectedOption={entry.messageType}
						/>
					</div>
					<div className="flex-1">
						<InputField
							handleChange={(e) => updateEntry(index, "greeting", e.target.value)}
							name={`greeting-${index}`}
							placeholder={ts("greetingPlaceholder")}
							showError={false}
							value={entry.greeting}
						/>
					</div>
					<button
						className="mt-2 text-text-meta hover:text-status-error"
						type="button"
						onClick={() => onChange(value.filter((_, i) => i !== index))}
					>
						<Icon className="w-4 h-4" icon={faTrash} />
					</button>
				</div>
			))}

			<button
				className="flex items-center gap-1 text-sm text-blue-action hover:underline self-start"
				type="button"
				onClick={() => onChange([...value, { ...emptyEntry }])}
			>
				<Icon className="w-3 h-3" icon={faPlus} />
				{ts("addRow")}
			</button>
		</div>
	);
};
